import { AsyncSubject, Observable } from 'rxjs';

import { ActivityDescription } from '../../mru/models/activity-description.model';
import { SharingType } from '../../mru/shared-with-me/shared-with-me.model';
import { InsightName, PotentialAction, TaskInsight, User } from '../../officefeed/models/myanalytics.model';
import { AttendeeEmailAddress, UpcomingMeetingAttendee } from '../../officefeed/models/upcoming-meetings.model';
import { InstrumentationData } from '../instrumentation/instrumentation-data.model';
import { InstrumentationParam, KnownAreas, KnownInstrumentationActions, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { EmailReference } from '../models/email-attachment.model';
import { FileItem, FileType, SourceProvenance } from '../models/file-item.model';
import { FormEnum, FormSubtypeCategory, FormType } from '../models/form.model';
import { KnownWorkloadReferralNames } from '../models/known-workload-referral-names.model';
import { MeetingArtifact } from '../models/meeting-object.model';
import { MruFileItemFormsMetaData } from '../models/mru-file-item-metadata.model';
import { GraphSharepointIds, Wopi } from '../models/onedrive-graph.model';
import { Person } from '../models/person.model';
import { AuthType } from '../shared-context/client-startup-config.service';
import { UrlEncoder } from '../utilities/url-encoder.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { ActionType, Activity } from './activity.model';
import { Analytics } from './analytics.model';
import { DocumentSharing } from './document-sharing.model';
import { MruStorageHost } from './mru-data.service';

export enum MruFileFormat {
    Recent,
    Shared,
    Trending,
    Recommended,
    ClassNoteBook,
    News,
    NoteBook,
    Form,
    OfficeFeedDocument,
    EdgeworthDocument,
    SharedForm,
    StreamsVideo,
    UpcomingMeeting,
    MyAnalytics
}

export interface DocumentThumbnails {
    small?: string;
    medium?: string;
    large?: string;
    recommendedThumbnail?: string;
    // Used for Forms, background color that would show behind a thumbnail png.
    backgroundColor?: string;
}

export interface ThumbnailInfo {
    thumbnailId?: string;
    // Url is usually added later to allow delay of image load, coined the process of inflation.
    thumbnailUrl?: string;
    thumbnailFillColor?: string;
}

export interface WopiHostData {
    bundleUrl?: string;
    bundleVersion?: string;
    downloadUrl: string;
    eTag: string;
    fileName: string;
    fileSize: number;
    irmEnabled: boolean;
    isNewFile: boolean;
    readOnly: boolean;
    wac: Wopi;
    sharepointIds?: GraphSharepointIds;
}

export class MruFileItem extends FileItem {
    // The original url returned by the API
    // This will be encoded for Shared with me and Discover APIs, but not encoded for MRU++
    public documentUrl: string;

    // Recent activities related to the item
    // TODO: consolidate with activity
    public activities: Array<Activity>;

    // Meta data per FileType, for now only forms require this
    public metaData: MruFileItemFormsMetaData;

    // Data needed to display the document in the wac
    public wopiData: WopiHostData;

    // OneDrive 'canonical' url. This is of the form https://<host>/<folder path>/<filename>
    // Ex: https://microsoft.sharepoint.com/teams/teamsix/Shared%20Documents/Office.com/OfficeHome"
    // This url is required for the share control and is also used by the folder component. It should be encoded.
    // TODO: Get trending API to return canonical url (or get share control to accept aspx urls). If that ever happens, remove this property
    // and corresponding code.
    public canonicalUrl: string;

    public lastModifiedActivity: Array<Activity>;
    public isMsftDogfood: boolean;
    public isOnPremOrVanity: boolean;

    public analytics: Analytics;
    public iconUrl: string;
    public isRedeemed: boolean;
    public extension?: string;
    public source?: string;

    public initialInflationCompleted: Observable<boolean>;
    private readonly _initialInflationCompleted: AsyncSubject<boolean>;

    private readonly mediaDurationRegex = new RegExp(/PT(\d+H)?(\d+M)?(\d*).*S?/); //regex for getting video length

    constructor(
        public format: MruFileFormat,
        elementId: string,
        impressionId: string,
        docId: string,
        driveId: string,
        fileName: string,
        title: string,
        url: string,
        webUrl: string,
        downloadUrl: string,
        lastModifiedTime: Date,
        lastAccessedTime: Date,
        sharedTime: Date,
        fileType: FileType,
        public sharingType: SharingType,
        provenance: SourceProvenance,
        pathArray: Array<string>,
        isRtl: boolean,
        authType: AuthType,
        public fromCache: boolean,
        public application: string,
        public resourceId: string,
        public storageHost: MruStorageHost,
        urlEncoded = false,
        public isPinned = false,
        lastModifiedBy: Person = null,
        containerUrl: string = null,
        public mruV2Category: string = null,
        public mruV2Type: string = null,
        public mruV2Id: string = null,
        public activity: ActivityDescription = null,
        public isEmailAttachment = false,
        isLocal: boolean = false,
        public createdDate: Date = null,
        public sharepointIds: GraphSharepointIds = null,
        public sharing?: DocumentSharing,
        public isNew?: boolean,
        public emailReference?: EmailReference,
        public sharedByUsername?: string,
        public sharedByEmail?: string,
        public sharedById?: string,
        public thumbnailInfo?: ThumbnailInfo,
        attribution?: string,
        public spoId?: string,
        public isCollab?: boolean,
        serviceId?: string,
        public navigationId?: string,
        public thumbnails?: DocumentThumbnails,
        public feedbackId?: string,
        // serverDocId of the item before it was onboarded to MRU, its original Id
        public serverDocId?: string,
        public createdBy?: string,
        public teamsDeepLink?: string,
        public ownerType?: FormType,
        public ownerId?: string,
        public isDeleted?: boolean,
        public isHidden?: boolean,
        public isSafeLink: boolean = false,
        public streamsThumbnailUrl?: string,
        // upcoming meeting
        public startTime?: string,
        public endTime?: string,
        public location?: string,
        public locations?: string,
        public attendees?: Array<UpcomingMeetingAttendee>,
        public joinUrl?: string,
        public organizer?: AttendeeEmailAddress,
        public joinMeetingText?: string,
        public mediaDuration?: string,
        // Added to identify the difference between a forms survey and poll
        public subType?: string,
        public subTypeCategory?: string,
        // myAnalytics
        public taskInsight?: TaskInsight,
        public potentialAction?: Array<PotentialAction>,
        public activityUser?: User,
        public insightName?: InsightName,
        public insightDescription?: string,
        public insightDescriptionPrefix?: string,
        public meetingArtifacts?: Array<MeetingArtifact>
    ) {
        super(elementId, impressionId, docId, driveId, fileName, title, url, webUrl, downloadUrl,
            lastModifiedTime, lastAccessedTime, sharedTime, fileType, provenance, pathArray, isRtl, urlEncoded, lastModifiedBy, containerUrl, isEmailAttachment, emailReference ? emailReference.AttachmentType : undefined, isLocal, isSafeLink);

        if (!sharepointIds) {
            this.sharepointIds = {
                listId: null,
                listItemId: null,
                listItemUniqueId: null,
                siteId: null,
                siteUrl: null,
                webId: null
            };
        }

        // Don't encode this as this is meant to be the id used when calling the MRU service to delete/pin documents.
        this.documentUrl = url;

        // The Trending API returns url that go directly to the Wopiframe.aspx/Doc.aspx page. For the share control, we need
        // the canonical url for the document, so we construct it from the container url and filename. Since the Trending API
        // doesn't return the file extension, we extract the filename with extension from the document url itself.
        // All other APIs return canonical urls.
        if (format === MruFileFormat.Trending) {
            // Example of expected url: https://microsoft.sharepoint.com/teams/SharedDesign/_layouts/15/Doc.aspx?sourcedoc=%7B32586AC7-A349-4B09-954A-FCA9888FD0B3%7D&file=Chat%20in%20O365.pptx&action=edit&mobileredirect=true&DefaultItemOpen=1"
            const groups = this.url.match(/file=([\S]+?)(&|$)/i);
            if (groups && groups.length >= 2) {
                const filenameWithExtension = groups[1];
                // filename will already be encoded
                this.canonicalUrl = `${UrlEncoder.encodeUrl(containerUrl)}/${filenameWithExtension}`;
            } else {
                this.canonicalUrl = this.url;
            }
            this.sharing = {
                shared: true
            };
        } else {
            this.canonicalUrl = this.url || (format === MruFileFormat.EdgeworthDocument && this.webUrl);
            if (format === MruFileFormat.Shared) {
                this.sharing = {
                    shared: true
                };
            }
        }

        if (this.format === MruFileFormat.StreamsVideo && this.mediaDuration) {
            const regResult = this.mediaDurationRegex.exec(this.mediaDuration);
            if (regResult) {
                // This assumes we can only have hours/minutes/seconds
                let hours = regResult[1];
                let minutes = regResult[2];
                let seconds = regResult[3];
                if (hours && hours.length < 3) {
                    hours = `0${hours}`;
                }
                if (minutes && minutes.length < 3) {
                    minutes = `0${minutes}`;
                }
                if (seconds && seconds.length < 2) {
                    seconds = `0${seconds}`;
                }
                this.mediaDuration = `${hours ? hours.substring(0, hours.length - 1) + ':' : ''}${minutes ? minutes.substring(0, minutes.length - 1) + ':' : ''}${seconds || ''}`;
            }
        }

        // Extracts the site URL from the document url as a fallback. This may get overwritten by the output of the Shares API call.
        if (url) {
            if (!this.sharepointIds.siteUrl) {
                const matches = url.match(/https?:\/\/.*?\.sharepoint(-df)?\.com\/.*?\/.*?\//);
                if (matches && matches.length) {
                    this.sharepointIds.siteUrl = matches[0];
                }
            }

            this.isMsftDogfood = url.lastIndexOf('microsoft-my.sharepoint-df.com') > -1 || url.lastIndexOf('microsoft.sharepoint-df.com') > -1;
        }

        if (FileItem.referralFromFileLocation(this.url) === KnownWorkloadReferralNames.SharePoint) {
            this.sharing = { shared: true };
        }

        if (this.lastModifiedBy && this.lastModifiedBy.fullName && this.lastModifiedBy.email && this.lastModifiedTime) {
            this.lastModifiedActivity = [{
                action: {
                    type: ActionType.Edit,
                    data: {}
                },
                actor: {
                    user: {
                        displayName: this.lastModifiedBy.fullName,
                        email: this.lastModifiedBy.email,
                        userPrincipalName: this.lastModifiedBy.email
                    },
                    id: null
                },
                timestamp: this.lastModifiedTime
            }];
        }

        this._initialInflationCompleted = new AsyncSubject<boolean>();
        this.initialInflationCompleted = this._initialInflationCompleted.asObservable();

        if (typeof attribution === 'string') {
            this.attribution = attribution;
        }

        // It seems all OnPrem docs with a service id will start with "ONPREM".
        // All O365 docs will have "O365".
        // If a service id isn't given, we will try to determine if the doc is a normal
        // Cloud SharePoint docs by looking at the URL hostname.
        // Consumer will also never be on prem or vanity
        if (authType === AuthType.MSA) {
            this.isOnPremOrVanity = false;
        } else if (serviceId && serviceId.lastIndexOf('ONPREM') >= 0) {
            this.isOnPremOrVanity = true;
        } else if ((serviceId && serviceId.lastIndexOf('O365') >= 0) || this.fileType === FileType.Sway || this.fileType === FileType.Forms) {
            this.isOnPremOrVanity = false;
        } else {
            const hostname = UrlParser.getHostName(this.url);
            if (hostname && hostname.lastIndexOf('sharepoint.com') === -1 && !this.isMsftDogfood) {
                this.isOnPremOrVanity = true;
            } else {
                this.isOnPremOrVanity = false;
            }
        }
    }

    public getFormEnumType(): FormEnum {
        if (this.subType) {
            if (this.subType.toLowerCase() === FormEnum.Form && this.subTypeCategory) {
                switch (this.subTypeCategory.toLowerCase()) {
                    case FormSubtypeCategory.Poll:
                    case FormSubtypeCategory.TeamsPoll:
                    case FormSubtypeCategory.PPTPoll:
                    case FormSubtypeCategory.WordPoll:
                        return FormEnum.Poll;

                    case FormSubtypeCategory.Assignments:
                        return FormEnum.Assignments;

                    default:
                        break;
                }
            } else if (this.subType.toLowerCase() === FormEnum.Poll) {
                return FormEnum.Poll;
            }
        }
        return FormEnum.Form;
    }

    public getClickData(eventName: KnownInstrumentationEvents, action: KnownInstrumentationActions, index: number): InstrumentationData {
        const result = super.getClickData(eventName, action, index, this.format === MruFileFormat.EdgeworthDocument);

        if (result && result.params) {
            result.params[InstrumentationParam.Area] = eventName === KnownInstrumentationEvents.SuggestedActionClick ? KnownAreas.SuggestedActions : (eventName === KnownInstrumentationEvents.RecommendedClick ? KnownAreas.Recommended : KnownAreas.Documents);

            if (typeof this.isPinned === 'boolean') {
                result.params[InstrumentationParam.Pinned] = this.isPinned.toString();
            }

            if (typeof this.isNew === 'boolean') {
                result.params[InstrumentationParam.ItemIsNew] = this.isNew.toString();
            }

            if (this.sharing && typeof this.sharing.shared === 'boolean') {
                result.params[InstrumentationParam.ItemIsShared] = this.sharing.shared.toString();
            }
        }

        return result;
    }

    public getClickDataForPath(
        eventName: KnownInstrumentationEvents,
        action: KnownInstrumentationActions,
        index: number): InstrumentationData {
        const result = super.getClickDataForPath(eventName, action, index);

        if (result && result.params) {
            result.params[InstrumentationParam.Area] = KnownAreas.Documents;
        }

        return result;
    }

    public copyInflationData(from: MruFileItem, mruActivityEnabled: boolean = false) {
        if (from.activities && !mruActivityEnabled) {
            this.activities = from.activities.slice();
        }

        if (from.driveId) {
            this.driveId = from.driveId;
        }

        if (typeof from.isNew !== 'undefined' && !mruActivityEnabled) {
            this.isNew = from.isNew;
        }

        if (from.sharepointIds.siteUrl) {
            this.sharepointIds.siteUrl = from.sharepointIds.siteUrl;
        }

        if (from.sharing && !mruActivityEnabled) {
            this.sharing = { ...from.sharing };
        }

        if (from.thumbnails) {
            this.thumbnails = { ...from.thumbnails };
        }

        if (from.wopiData) {
            this.wopiData = { ...from.wopiData };
        }
    }

    public inflationCompleted() {
        this._initialInflationCompleted.next(true);
        this._initialInflationCompleted.complete();
    }

    public shouldAddLoginHint() {
        // Only add login_hint to ODSP and OWA URLs for now. Once all apps support login_hint add to them too.
        // login_hint is added to make sure the right identity is used when account switching is enabled.
        // For Edgeworth files, we check the 'source' property, but for MRU++ files we use the file type
        return (!this.isLocal
            && !this.isOnPremOrVanity
            && ((this.source === 'OneDriveForBusiness' || this.source === 'SharePoint' || this.source === 'EmailAttachment')
                || (this.fileType !== FileType.Sway
                    && this.fileType !== FileType.Forms
                    && this.fileType !== FileType.PowerBI)));
    }
}
